/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import { getTriAppConfig } from "../../TriAppConfig";

const FLOORPLAN_API_URL = "/p/floorplans";
const FLOOR_ID_PARAM_NAME = "recordId";
const FLOORPLAN_ID_PARAM_NAME = "drawingId";
const LABEL_ID_PARAM_NAME = "labelId";
const ATTACHED_IDS_URL = `/attachedIds`;
const GET_CENTER_POINTS_URL = `${FLOORPLAN_API_URL}/spaceCenterPoints`;
const UPLOAD_URL = `${ FLOORPLAN_API_URL }/upload`;
const QUERY_URL = `${ FLOORPLAN_API_URL }/drawings`;
const DETAILS_URL = `${ FLOORPLAN_API_URL }/details`;
const LAYERS_URL = `/layers`;
const REPORT_URL = `${FLOORPLAN_API_URL }/graphicReport`;
const SVG_LEGEND_URL = `${ FLOORPLAN_API_URL }/svglegend`;
const GET_LAYER_URL = `/layers`;
const GET_FLOORPLAN_LABELSLAYER_API_URL = `${FLOORPLAN_API_URL}/labellayers`;

const floorplanIdMap = new Map();
const floorplanMap = new Map();
const floorplanCenterPointsMap = new Map();
const floorplanLabelsMap = new Map();
const floorplanLayersMap = new Map();

const SOURCE_ATTACHED_IDS = "SOURCE_ATTACHED_IDS";
const SOURCE_CENTER_POINTS = "SOURCE_CENTER_POINTS";
const SOURCE_DRAWING_ATTRIBUTES = "SOURCE_DRAWING_ATTRIBUTES";
const SOURCE_DRAWING_DELETE = "SOURCE_DRAWING_DELETE";
const SOURCE_LABEL_LAYER = "SOURCE_LABEL_LAYER";
const SOURCE_DRAWING_LIST = "SOURCE_DRAWING_LIST";
const SOURCE_FLOORPLAN = "SOURCE_FLOORPLAN";
const SOURCE_FLOORPLAN_ID = "SOURCE_FLOORPLAN_ID";
const SOURCE_GRAPHIC_REPORTS = "SOURCE_GRAPHIC_REPORTS";
const SOURCE_LAYER = "SOURCE_LAYER";
const SOURCE_LAYERS = "SOURCE_LAYERS";
const SOURCE_LOAD_DXF = "SOURCE_LOAD_DXF";
const SOURCE_SVG_LEGEND = "SOURCE_SVG_LEGEND";

export function cacheFloorplan(floorplanId, floorplan) {
  floorplanMap.set(floorplanId, floorplan);
}

async function getFloorplanId( floorId )
{
    if (!floorId) return null;
    let floorplanId = floorplanIdMap.get(floorId);
    if (floorplanId !== undefined) return floorplanId;

    const getFloorplanIdUrl = new URL(
        `${getTriAppConfig().tririgaUrl}${FLOORPLAN_API_URL}`
    );
    getFloorplanIdUrl.searchParams.set(FLOOR_ID_PARAM_NAME, floorId);
    const getFloorplanIdResponse = await fetch(new Request(getFloorplanIdUrl), {
        credentials: "include",
    });
    if( !getFloorplanIdResponse.ok )
    {
        getFloorplanIdResponse.source = SOURCE_FLOORPLAN_ID;
        throw getFloorplanIdResponse;
    }
    floorplanId = (await getFloorplanIdResponse.json()).drawingId;
    floorplanIdMap.set(floorId, floorplanId);
    return floorplanId;
}

async function getFloorplan( floorplanId )
{
    if (!floorplanId) return null;
    let floorplan = floorplanMap.get(floorplanId);
    if (floorplan !== undefined) return floorplan;

    const getFloorplanUrl = `${
      getTriAppConfig().tririgaUrl
    }${FLOORPLAN_API_URL}/${floorplanId}`;
    const getFloorplanResponse = await fetch(new Request(getFloorplanUrl), {
      credentials: "include",
    });
    if( !getFloorplanResponse.ok )
    {
        getFloorplanResponse.source = SOURCE_FLOORPLAN;
        throw getFloorplanResponse;
    }
    floorplan = await getFloorplanResponse.text();
    cacheFloorplan(floorplanId, floorplan);
    return floorplan;
}

async function uploadDXFFile( dxfFile )
{
  let url = `${ getTriAppConfig().tririgaUrl }${ UPLOAD_URL }`;
  url += `?path=${ encodeURIComponent( dxfFile.name ) }`;
  const drawing = await fetch( url,
    {
        method: "PUT",
        headers: {
           'Accept': 'application/json',
        },
        credentials: 'include',
        body: dxfFile
        }
    ).then(
        ( response ) =>
            {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_LOAD_DXF;
                throw response;
            }
            return response.text();
        }
    ).then(
        ( body ) => {
            try
            {
                return JSON.parse( body );
            }
            catch( e )
            {
                const err = {
                    message: e.message,
                    body: body,
                };
                throw err;
            }
        }
    ).catch(
        err => { throw err; }
    );
    return drawing;
}

async function queryDrawings( query )
{
    const url = `${ getTriAppConfig().tririgaUrl }${ QUERY_URL }`;

    let queryString = "";
    if( query ) queryString = JSON.stringify( query );

    const drawings = await fetch( url,
    {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: queryString,
    }
    ).then(
        ( response ) =>
        {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_DRAWING_LIST;
                throw response;
            }
            return response.text();
        }
    ).then(
        ( body ) =>
        {
            try
            {
                return JSON.parse( body );
            }
            catch( e )
            {
                const err = {
                    message: e.message,
                    body: body,
                    cause: response,
                };
                throw err;
            }
        }
    );
    return drawings;
}

async function getDrawingAttributes( drawingId )
{
    let url = `${ getTriAppConfig().tririgaUrl }${ DETAILS_URL }/`;
    url += drawingId;

    const drawings = await fetch( url,
        {
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'include',
        }
    ).then(
        ( response ) =>
        {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_DRAWING_ATTRIBUTES;
                throw response;
            }
            return response.text();
        }
    ).then(
        (body) => {
            try {
                return JSON.parse(body);
            }
            catch (e) {
                const err = {
                    message: e.message,
                    body: body,
                    cause: response,
                };
                throw err;
            }
        }
    ).catch(
        err => { throw err; }
    );
    return drawings;
}

export async function getLayers(
    drawingId
) {
    if( !drawingId ) return null;
    let layers = floorplanLayersMap.get( drawingId );
    if( layers !== undefined ) return layers;

    const url = `${ getTriAppConfig().tririgaUrl }${ FLOORPLAN_API_URL }/${ drawingId }${ LAYERS_URL }`;

    layers = await fetch(url,
        {
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'include',
        }
    ).then(
        (response) => {
            if (response.redirected) {
                location.href = response.url;
                return;
            }
            if (!response.ok) {
                response.source = SOURCE_LAYERS;
                throw response;
            }
            return response.text();
        }
    ).then(
        (body) => {
            try {
                return JSON.parse(body);
            }
            catch (e) {
                const err = {
                    message: e.message,
                    body: body,
                    cause: response,
                };
                throw err;
            }
        }
    ).catch(
        err => { throw err; }
    );
    cacheLayer( drawingId, layers );
    return layers;
}

export async function getAttacheIdsAndLabels(
    drawingId
) {
    if( !drawingId ) return null;

    const url = `${ getTriAppConfig().tririgaUrl }${ FLOORPLAN_API_URL }/${ drawingId }${ ATTACHED_IDS_URL }`;

    const ids = await fetch( url,
        {
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'include',
        }
    ).then(
        ( response ) =>
        {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_ATTACHED_IDS;
                throw response;
            }
            return response.text();
        }
    ).then(
        ( body ) =>
        {
            try
            {
                return JSON.parse( body );
            }
            catch( e )
            {
                const err = {
                    message: e.message,
                    body: body,
                    cause: response,
                };
                throw err;
            }
        }
    ).catch(
        err => { throw err; }
    );
    return ids;
}

export async function getGraphicReport(
    drawingId,
    reportId
) {
    if (!drawingId) return null;
    if (!reportId) return null;

    const url = `${getTriAppConfig().tririgaUrl}${REPORT_URL}/${drawingId}/${reportId}`;

    const report = await fetch(url,
      {
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include',
      }
    ).then(
      (response) => {
        if (response.redirected) {
            location.href = response.url;
            return;
        }
        if (!response.ok) {
            response.source = SOURCE_GRAPHIC_REPORTS;
            throw response;
        }
        return response.text();
      }
    ).then(
        (body) => {
          try {
              return JSON.parse(body);
          }
          catch (e) {
              const err = {
                  message: e.message,
                  body: body,
                  cause: response,
              };
              throw err;
          }
        }
    ).catch(
        err => { throw err; }
    );
    return report;
}


export async function getSVGLegend( request )
{
  if( !request ) return null;
  if( !request.drawingId ) return null;
  if( !request.reportId || request.reportId === 0 ) return null;

  const url = `${ getTriAppConfig().tririgaUrl }${ SVG_LEGEND_URL }`;

  const svg = await fetch( url,
    {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify( request )
    }
    ).then(
        ( response ) =>
        {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_SVG_LEGEND;
                throw response;
            }
            return response.text();
        }
    ).then( ( body ) =>
        {
            try
            {
                return body;
            }
            catch( e )
            {
                const err = {
                    message: e.message,
                    body:    body,
                    cause:   response,
                };
                throw err;
            }
        }
    ).catch(
        err => { throw err; }
    );
    return svg;
}

export function cacheLayer( floorplanId, layers )
{
    floorplanLayersMap.set( floorplanId, layers );
}


export async function getLayer(
    drawingId, layerName
) {
    if( !drawingId || !layerName  ) return null;

    const url = `${ getTriAppConfig().tririgaUrl }${ FLOORPLAN_API_URL }/${ drawingId }${ GET_LAYER_URL }/${ layerName }`;

    const layer = await fetch(url,
        {
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'include',
        }
    ).then(
        (response) => {
            if (response.redirected) {
                location.href = response.url;
                return;
            }
            if (!response.ok) {
                response.source = SOURCE_LAYER;
                throw response;
            }
            return response.text();
        }
    ).catch(
        err => { throw err; }
    );
    return layer;
}

async function deleteDrawing( drawingId )
{
    let url = `${ getTriAppConfig().tririgaUrl }${ FLOORPLAN_API_URL }/`;
    url += drawingId;

    await fetch( url,
        {
            method: "DELETE",
            headers: {
                'Accept': 'application/json',
            },
            credentials: 'include',
        }
    ).then(
        ( response ) =>
        {
            if( response.redirected )
            {
                location.href = response.url;
                return;
            }
            if( !response.ok )
            {
                response.source = SOURCE_DRAWING_DELETE;
                throw response;
            }
        }
    )
}


export function cacheCenterPoints(floorplanId, centerPoints) {
  floorplanCenterPointsMap.set(floorplanId, centerPoints);
}

/**
 * Gets the center points for all spaces in the floor plan.
 */
async function getCenterPoints(floorplanId) {
    if (!floorplanId) return null;
    let centerPointsPromise = floorplanCenterPointsMap.get(floorplanId);
    if (centerPointsPromise !== undefined) return centerPointsPromise;
    centerPointsPromise = fetchCenterPoints(floorplanId);
    cacheCenterPoints(floorplanId, centerPointsPromise);
    return centerPointsPromise;
}

async function fetchCenterPoints(floorplanId) {
    const getCenterPointsUrl = new URL(
      `${getTriAppConfig().tririgaUrl}${GET_CENTER_POINTS_URL}`
    );
    getCenterPointsUrl.searchParams.set(FLOORPLAN_ID_PARAM_NAME, floorplanId);
    const getCenterPointsResponse = await fetch(new Request(getCenterPointsUrl), {
      credentials: "include",
    });
  if( !getCenterPointsResponse.ok )
  {
        getCenterPointsResponse.source = SOURCE_CENTER_POINTS;
        throw getCenterPointsResponse;
    }
    let centerPointsArray = await getCenterPointsResponse.json();
    centerPointsArray = centerPointsArray == null ? [] : centerPointsArray;
    const centerPoints = {};
    for (let i = 0; i < centerPointsArray.length; i += 1) {
        centerPoints[centerPointsArray[i].recordId] = centerPointsArray[i];
    }
    return centerPoints;
}

async function getLabelsLayer(floorplanId, labelId) {
  if (!floorplanId || !labelId) return null;
  let floorplanLabels = floorplanLabelsMap.get( "" + floorplanId + labelId );
  if (floorplanLabels !== undefined) return floorplanLabels;

  const getLabelsLayerUrl = new URL(
    `${getTriAppConfig().tririgaUrl}${GET_FLOORPLAN_LABELSLAYER_API_URL}`
  );

  getLabelsLayerUrl.searchParams.set(FLOORPLAN_ID_PARAM_NAME, floorplanId);
  getLabelsLayerUrl.searchParams.set(LABEL_ID_PARAM_NAME, labelId);
  const getLabelsLayerResponse = await fetch(new Request(getLabelsLayerUrl), {
    credentials: "include",
  });
  if( !getLabelsLayerResponse.ok )
  {
      getLabelsLayerResponse.source = SOURCE_LABEL_LAYER;
      throw new Error( "Request to get the floor plan labels layer failed",
      { cause: getLabelsLayerResponse } );
  }
  floorplanLabels = await getLabelsLayerResponse.text();
  cacheLabelsLayer( "" + floorplanId + labelId, floorplanLabels);
  return floorplanLabels;
}

export function cacheLabelsLayer(floorplanId, floorplanLabels) {
  floorplanLabelsMap.set(floorplanId, floorplanLabels);
}

export default {
    getAttacheIdsAndLabels,
    getGraphicReport,
    getFloorplanId,
    getFloorplan,
    uploadDXFFile,
    queryDrawings,
    getDrawingAttributes,
    getLayers,
    getLayer,
    getSVGLegend,
    deleteDrawing,
    getCenterPoints,
    getLabelsLayer,
};
